import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Grid,
  Typography,
  Card,
  CardContent,
  Checkbox,
  Chip,
  Button,
  Paper,
} from "@material-ui/core";
import PlayArrowIcon from "@material-ui/icons/PlayArrow";
import RefreshIcon from "@material-ui/icons/Refresh";
import CompareArrowsIcon from "@material-ui/icons/CompareArrows";
import HistoryIcon from "@material-ui/icons/History";

import { actualizarHorasSalidas } from "../../../../../data-store/actions/events-actions";
import { segundosATiempo } from "../../../../../services/utils";
import SimpleDialogDemo from "../../../Modals/SimpleDialog";
import StartClocksHistoryView from "./StartClocksHistoryView";
import { agregarHistorial } from "./startClocksHistoryStorage";

const SIN_INICIAR = "0000-00-00 00:00:00";

// Mismo formato que ya usa SpecificStart.jsx/fechahoraGuionAndMiliseconds
// ("YYYY-MM-DD HH:mm:ss.SSS"), pero a partir de un Date arbitrario — acá
// hace falta porque "Igualar" usa como referencia una hora ya pasada, no
// necesariamente "ahora".
function formatearFechaHoraCompleta(date) {
  const dd = String(date.getDate()).padStart(2, "0");
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const yyyy = date.getFullYear();
  const h = String(date.getHours()).padStart(2, "0");
  const m = String(date.getMinutes()).padStart(2, "0");
  const s = String(date.getSeconds()).padStart(2, "0");
  const ms = String(date.getMilliseconds()).padStart(3, "0");
  return `${yyyy}-${mm}-${dd} ${h}:${m}:${s}.${ms}`;
}

function esIniciada(hS) {
  return !!hS && !hS.startsWith("0000");
}

// Aplana eventSubEvents (modalidad_X.hSalidas.serie_Y) en una lista
// ordenada por número de modalidad y de serie, con todo lo que necesita
// la tarjeta de cada serie.
function construirSeries(eventSubEvents) {
  const lista = [];
  Object.keys(eventSubEvents || {})
    .sort((a, b) => Number(a.split("_")[1]) - Number(b.split("_")[1]))
    .forEach((modalidadKey) => {
      const modalidad = eventSubEvents[modalidadKey];
      const hSalidas = modalidad && modalidad.hSalidas;
      if (!hSalidas) return;

      Object.keys(hSalidas)
        .sort((a, b) => Number(a.split("_")[1]) - Number(b.split("_")[1]))
        .forEach((serieKey) => {
          const salida = hSalidas[serieKey];
          lista.push({
            key: `${modalidadKey}|${serieKey}`,
            modalidadKey,
            serieKey,
            descripcionModalidad: modalidad.descripcionModalidad,
            descripcionSerie: salida.descripcionSerie,
            hS: salida.hS,
            hsTimeStamp: salida.hsTimeStamp,
            iniciada: esIniciada(salida.hS),
          });
        });
    });
  return lista;
}

export default function StartClocksView({ eventoIndex }) {
  const dispatch = useDispatch();
  const eventSubEvents = useSelector((state) => state.events.eventSubEvents);

  const [seleccionados, setSeleccionados] = useState({});
  // El valor no se usa en el render — existe solo para forzar un
  // re-render cada segundo y que el cronómetro recalcule el tiempo
  // transcurrido desde hsTimeStamp.
  const [, setTick] = useState(0);

  // Hace avanzar los cronómetros en vivo (un re-render por segundo), sin
  // depender de un listener nuevo de Firebase — el tiempo transcurrido se
  // recalcula cada vez desde hsTimeStamp.
  useEffect(() => {
    const interval = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(interval);
  }, []);

  const series = useMemo(() => construirSeries(eventSubEvents), [eventSubEvents]);

  const seleccionadosList = series.filter((s) => seleccionados[s.key]);

  function toggleSeleccion(key) {
    setSeleccionados((prev) => ({ ...prev, [key]: !prev[key] }));
  }

  const haySeleccionados = seleccionadosList.length > 0;
  const haySeleccionadosIniciados = seleccionadosList.some((s) => s.iniciada);
  const haySeleccionadosNoIniciados = seleccionadosList.some((s) => !s.iniciada);

  function limpiarSeleccion() {
    setSeleccionados({});
  }

  function iniciar() {
    const ahora = new Date();
    const hsTexto = formatearFechaHoraCompleta(ahora);
    const hsTimeStamp = ahora.getTime();
    const movimientoTs = Date.now();

    const updates = {};
    const historial = [];
    seleccionadosList.forEach((s) => {
      updates[`${s.modalidadKey}/hSalidas/${s.serieKey}/hS`] = hsTexto;
      updates[`${s.modalidadKey}/hSalidas/${s.serieKey}/hsTimeStamp`] = hsTimeStamp;
      historial.push({
        eventoId: eventoIndex,
        modalidadKey: s.modalidadKey,
        serieKey: s.serieKey,
        descModalidad: s.descripcionModalidad,
        descSerie: s.descripcionSerie,
        accion: "iniciar",
        hs: hsTexto,
        hsTimestamp: hsTimeStamp,
        movimientoTs,
      });
    });

    // No se espera la confirmación de Firebase antes de seguir — el
    // historial local ya deja la hora guardada aunque falle la red.
    dispatch(actualizarHorasSalidas(eventoIndex, updates));
    agregarHistorial(eventoIndex, historial);
    limpiarSeleccion();
  }

  function reiniciar() {
    const afectados = seleccionadosList.filter((s) => s.iniciada);
    if (afectados.length === 0) return;
    if (
      !window.confirm(
        `¿Seguro que quieres reiniciar ${afectados.length} crono(s)? Se borrará la hora de inicio.`
      )
    ) {
      return;
    }

    const movimientoTs = Date.now();
    const updates = {};
    const historial = [];
    afectados.forEach((s) => {
      updates[`${s.modalidadKey}/hSalidas/${s.serieKey}/hS`] = SIN_INICIAR;
      updates[`${s.modalidadKey}/hSalidas/${s.serieKey}/hsTimeStamp`] = 0;
      historial.push({
        eventoId: eventoIndex,
        modalidadKey: s.modalidadKey,
        serieKey: s.serieKey,
        descModalidad: s.descripcionModalidad,
        descSerie: s.descripcionSerie,
        accion: "reiniciar",
        hs: null,
        hsTimestamp: null,
        movimientoTs,
      });
    });

    dispatch(actualizarHorasSalidas(eventoIndex, updates));
    agregarHistorial(eventoIndex, historial);
    limpiarSeleccion();
  }

  function igualar() {
    const iniciados = seleccionadosList.filter((s) => s.iniciada);
    const noIniciados = seleccionadosList.filter((s) => !s.iniciada);
    if (iniciados.length === 0 || noIniciados.length === 0) return;

    // Referencia: el que ya inició más temprano entre los seleccionados.
    const referencia = iniciados.reduce((min, s) =>
      s.hsTimeStamp < min.hsTimeStamp ? s : min
    );

    const movimientoTs = Date.now();
    const updates = {};
    const historial = [];
    noIniciados.forEach((s) => {
      updates[`${s.modalidadKey}/hSalidas/${s.serieKey}/hS`] = referencia.hS;
      updates[`${s.modalidadKey}/hSalidas/${s.serieKey}/hsTimeStamp`] =
        referencia.hsTimeStamp;
      historial.push({
        eventoId: eventoIndex,
        modalidadKey: s.modalidadKey,
        serieKey: s.serieKey,
        descModalidad: s.descripcionModalidad,
        descSerie: s.descripcionSerie,
        accion: "igualar",
        hs: referencia.hS,
        hsTimestamp: referencia.hsTimeStamp,
        movimientoTs,
      });
    });

    dispatch(actualizarHorasSalidas(eventoIndex, updates));
    agregarHistorial(eventoIndex, historial);
    limpiarSeleccion();
  }

  return (
    <div style={{ paddingBottom: haySeleccionados ? 140 : 24 }}>
      <Grid container alignItems="center" justifyContent="space-between" style={{ marginBottom: 16 }}>
        <Grid item>
          <Typography variant="h5">Cronos / Relojes</Typography>
        </Grid>
        <Grid item>
          <SimpleDialogDemo icon={<HistoryIcon />}>
            <StartClocksHistoryView eventoIndex={eventoIndex} />
          </SimpleDialogDemo>
        </Grid>
      </Grid>

      <Grid container spacing={2}>
        {series.map((s) => {
          const seleccionado = !!seleccionados[s.key];
          const elapsedMs = s.iniciada
            ? Math.max(Date.now() - s.hsTimeStamp, 0)
            : 0;
          const cronometro = s.iniciada
            ? segundosATiempo(Math.floor(elapsedMs / 1000))
            : "00:00:00";

          return (
            <Grid item xs={12} sm={12} md={4} key={s.key}>
              <Card
                onClick={() => toggleSeleccion(s.key)}
                style={{
                  cursor: "pointer",
                  backgroundColor: "#e0e0e0",
                  border: seleccionado ? "2px solid #ff4400" : "1px solid #e0e0e0",
                  borderRadius: 8,
                }}
              >
                <CardContent>
                  <Grid container alignItems="flex-start" wrap="nowrap">
                    <Checkbox
                      checked={seleccionado}
                      color="primary"
                      onClick={(e) => e.stopPropagation()}
                      onChange={() => toggleSeleccion(s.key)}
                      style={{ padding: 0, marginRight: 8, marginTop: 2 }}
                    />
                    <div style={{ flex: 1 }}>
                      <Typography variant="h6" style={{ fontWeight: 800 }}>
                        {s.descripcionModalidad}
                      </Typography>
                      <Typography variant="h5" color="textSecondary">
                        {s.descripcionSerie}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {s.iniciada
                          ? new Date(s.hsTimeStamp).toLocaleString()
                          : "Sin iniciar"}
                      </Typography>
                      <Typography
                        variant="h4"
                        style={{
                          fontFamily: "monospace",
                          fontWeight: 700,
                          fontVariantNumeric: "tabular-nums",
                          marginTop: 6,
                        }}
                      >
                        {cronometro}
                      </Typography>
                    </div>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      {haySeleccionados && (
        <Paper
          elevation={4}
          style={{
            position: "fixed",
            bottom: 0,
            left: 0,
            right: 0,
            padding: 16,
            zIndex: 1300,
          }}
        >
          <Grid container spacing={1} style={{ marginBottom: 10 }}>
            {seleccionadosList.map((s) => (
              <Grid item key={s.key}>
                <Chip
                  label={`${s.descripcionModalidad} - ${s.descripcionSerie}`}
                  onDelete={() => toggleSeleccion(s.key)}
                />
              </Grid>
            ))}
          </Grid>
          <Grid container spacing={2}>
            <Grid xs={12} sm={4} item>
              <Button
                variant="contained"
                onClick={iniciar}
                style={{ backgroundColor: "#2e7d32", color: "#fff", padding:20, width:"100%" }}
              >
                <PlayArrowIcon style={{ marginRight: 6 }} />
                Iniciar
              </Button>
            </Grid>
            {haySeleccionadosIniciados && (
              <Grid xs={12} sm={4} item>
                <Button
                  variant="outlined"
                  onClick={reiniciar}
                  
                  style={{ borderColor: "#c62828", color: "#c62828", padding:20, width:"100%" }}
                >
                  <RefreshIcon style={{ marginRight: 6 }} />
                  Reiniciar
                </Button>
              </Grid>
            )}
            {haySeleccionadosIniciados && haySeleccionadosNoIniciados && (
              <Grid xs={12} sm={4} item>
                <Button
                  variant="outlined"
                  onClick={igualar}
                  style={{ borderColor: "#ff8f00", color: "#ff8f00", padding:20, width:"100%" }}
                >
                  <CompareArrowsIcon style={{ marginRight: 6 }} />
                  Igualar tiempos
                </Button>
              </Grid>
            )}
          </Grid>
        </Paper>
      )}
    </div>
  );
}
